// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  ChevronsUpDown,
  Users,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SystemId } from "@/components/ui/system-id";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Activity } from "@/hooks/api/use-account-activities";
import type { Email } from "@/hooks/api/use-email-thread";
import { useEmailThread } from "@/hooks/api/use-email-thread";
import { useTouchpointPersons } from "@/hooks/api/use-touchpoint-persons";
import { useTouchpointIdSidebarEnabled } from "@/hooks/use-feature-flags";
import { formatDate, formatFullDate } from "@/lib/date-formatting";

interface EmailThreadProps {
  activity: Activity;
  expandedEmailId: string;
  accountId?: string;
  getNameByEmailOverride?: (email?: string | null) => string;
}

function formatDateRange(start: string, end: string) {
  return `${formatDate(start)} - ${formatDate(end)}`;
}

interface ThreadSummaryProps {
  channel: string;
  threadSummary?: {
    number_of_messages: number;
    first_message_date: string;
    last_message_date: string;
  };
}

function ThreadSummary({ channel, threadSummary }: ThreadSummaryProps) {
  if (!threadSummary)
    return (
      <p className="text-sm text-muted-foreground mt-2">
        <Badge
          variant="secondary"
          className="bg-accent hover:bg-accent text-accent-foreground"
        >
          {channel}
        </Badge>
      </p>
    );

  return (
    <p className="text-sm text-muted-foreground mt-2">
      <Badge
        variant="secondary"
        className="bg-accent hover:bg-accent text-accent-foreground"
      >
        {channel}
      </Badge>
      {" • "}
      {threadSummary.number_of_messages}{" "}
      {threadSummary.number_of_messages === 1 ? "message" : "messages"}
      {" • "}
      {formatDateRange(
        threadSummary.first_message_date,
        threadSummary.last_message_date,
      )}
    </p>
  );
}

function EmailMessage({
  email,
  isExpanded,
  onToggle,
  showSeparator = true,
  direction,
  isSelected,
  accountId,
  getNameByEmailOverride,
}: {
  email: Email;
  isExpanded: boolean;
  onToggle: () => void;
  showSeparator?: boolean;
  direction?: string;
  isSelected?: boolean;
  accountId?: string;
  getNameByEmailOverride?: (email?: string | null) => string;
}) {
  const { getNameByEmail: getNameByEmailFromApi } = useTouchpointPersons(
    getNameByEmailOverride ? undefined : accountId,
  );
  const getNameByEmail = getNameByEmailOverride ?? getNameByEmailFromApi;
  const showTouchpointId = useTouchpointIdSidebarEnabled();

  // Format the preview text by removing newlines and extra spaces
  const previewText = useMemo(() => {
    if (!email.body) return "";
    return email.body.replace(/\n+/g, " ").replace(/\s+/g, " ").trim();
  }, [email.body]);

  // Get display name for sender
  const senderName = useMemo(() => {
    return {
      name: getNameByEmail(email.from_email),
      email: email.from_email,
    };
  }, [email.from_email, getNameByEmail]);

  // Get display names for recipients
  const recipientNames = useMemo(() => {
    return {
      to: email.to_emails.map((email) => ({
        name: getNameByEmail(email),
        email,
      })),
      cc:
        email.cc_emails?.map((email) => ({
          name: getNameByEmail(email),
          email,
        })) || [],
      bcc:
        email.bcc_emails?.map((email) => ({
          name: getNameByEmail(email),
          email,
        })) || [],
    };
  }, [email.to_emails, email.cc_emails, email.bcc_emails, getNameByEmail]);

  function renderRecipients() {
    const recipients: React.ReactNode[] = [];

    recipients.push(
      <p key="to" className="text-xs text-muted-foreground">
        to:{" "}
        {recipientNames.to.map(({ name, email }, index) => (
          <React.Fragment key={email}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{name}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{email}</p>
              </TooltipContent>
            </Tooltip>
            {index < recipientNames.to.length - 1 ? ", " : ""}
          </React.Fragment>
        ))}
      </p>,
    );

    if (recipientNames.cc.length > 0) {
      recipients.push(
        <p key="cc" className="text-xs text-muted-foreground">
          CC:{" "}
          {recipientNames.cc.map(({ name, email }, index) => (
            <React.Fragment key={email}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{name}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{email}</p>
                </TooltipContent>
              </Tooltip>
              {index < recipientNames.cc.length - 1 ? ", " : ""}
            </React.Fragment>
          ))}
        </p>,
      );
    }

    if (recipientNames.bcc.length > 0) {
      recipients.push(
        <p key="bcc" className="text-xs text-muted-foreground">
          BCC:{" "}
          {recipientNames.bcc.map(({ name, email }, index) => (
            <React.Fragment key={email}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{name}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{email}</p>
                </TooltipContent>
              </Tooltip>
              {index < recipientNames.bcc.length - 1 ? ", " : ""}
            </React.Fragment>
          ))}
        </p>,
      );
    }

    return recipients;
  }

  return (
    <>
      <div className="relative">
        {direction && (
          <div className="absolute -left-6 top-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                {direction === "IN" ? (
                  isSelected ? (
                    <div className="bg-primary rounded-full">
                      <ArrowRightCircle className="h-4 w-4 text-background" />
                    </div>
                  ) : (
                    <ArrowRightCircle className="h-4 w-4 text-primary" />
                  )
                ) : isSelected ? (
                  <div className="bg-muted-foreground/40 rounded-full">
                    <ArrowLeftCircle className="h-4 w-4 text-background" />
                  </div>
                ) : (
                  <ArrowLeftCircle className="h-4 w-4 text-muted-foreground/40" />
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{direction === "IN" ? "Inbound" : "Outbound"} email</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        <div className="space-y-2 rounded-md">
          <div
            className="flex justify-between items-baseline cursor-pointer rounded-sm px-2 py-1 -mx-2"
            onClick={onToggle}
          >
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-sm font-medium cursor-default">
                    {senderName.name}
                    {isExpanded && (
                      <span className="text-muted-foreground ml-2 text-xs">
                        &lt;{senderName.email}&gt;
                      </span>
                    )}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{senderName.email}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatFullDate(email.timestamp)}
            </p>
          </div>

          {isExpanded ? (
            <>
              <div className="space-y-1">{renderRecipients()}</div>
              <div className="mt-2 text-sm whitespace-pre-wrap break-words rounded-md py-2">
                {email.body}
              </div>
              {showTouchpointId && (
                <div className="flex justify-end mt-2">
                  <SystemId id={email.id} />
                </div>
              )}
            </>
          ) : (
            <div
              className="text-sm text-muted-foreground truncate cursor-pointer rounded-sm px-2 py-1 -mx-2"
              onClick={onToggle}
            >
              {previewText}
            </div>
          )}
        </div>
      </div>
      {showSeparator && <Separator className="my-4" />}
    </>
  );
}

function ThreadParticipants({
  participants,
  accountId,
  getNameByEmailOverride,
}: {
  participants: string[];
  accountId?: string;
  getNameByEmailOverride?: (email?: string | null) => string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getNameByEmail: getNameByEmailFromApi } = useTouchpointPersons(
    getNameByEmailOverride ? undefined : accountId,
  );
  const getNameByEmail = getNameByEmailOverride ?? getNameByEmailFromApi;

  const participantNames = useMemo(() => {
    return participants.map((email) => ({
      name: getNameByEmail(email),
      email,
    }));
  }, [participants, getNameByEmail]);

  const displayParticipants = isExpanded
    ? participantNames
    : participantNames.slice(0, 3);
  const hasMore = participantNames.length > 3;

  if (!participants.length) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        <span>Active Participants</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {displayParticipants.map(({ name, email }) => (
          <React.Fragment key={email}>
            {name !== email ? (
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant="secondary"
                    className="bg-accent hover:bg-accent text-accent-foreground cursor-default"
                  >
                    {name}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{email}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Badge
                variant="secondary"
                className="bg-accent hover:bg-accent text-accent-foreground"
              >
                {email}
              </Badge>
            )}
          </React.Fragment>
        ))}
        {!isExpanded && hasMore && (
          <button
            onClick={() => setIsExpanded(true)}
            className="text-sm text-muted-foreground hover:text-foreground shrink-0"
          >
            +{participantNames.length - 3} more
          </button>
        )}
      </div>
    </div>
  );
}

export function EmailThread({
  activity,
  expandedEmailId,
  accountId,
  getNameByEmailOverride,
}: EmailThreadProps) {
  const { data: emailThread } = useEmailThread(activity.id);
  const emails = useMemo(
    () => emailThread?.emails || [],
    [emailThread?.emails],
  );
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(() => {
    if (emails?.length) {
      return new Set([expandedEmailId]);
    }
    return new Set();
  });
  const [selectedEmailId, setSelectedEmailId] =
    useState<string>(expandedEmailId);
  const [showCollapsed, setShowCollapsed] = useState(false);
  const emailsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If we have thread data, update the expandedEmails and selectedEmailId
    // This will handle both initial load and subsequent expandedEmailId changes
    if (emails?.length) {
      setExpandedEmails((prev) => {
        const next = new Set(prev);
        next.add(expandedEmailId);
        return next;
      });
      setSelectedEmailId(expandedEmailId);
    }
  }, [emails, expandedEmailId]);

  // Add useEffect to scroll to the selected email when expandedEmailId changes
  useEffect(() => {
    if (!emails?.length) return;

    // First ensure the email is expanded
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      next.add(expandedEmailId);
      return next;
    });

    // Wait for the rendering to complete
    setTimeout(() => {
      // Find the element for the selected email
      const selectedElement = document.querySelector(
        `[data-email-id="${expandedEmailId}"]`,
      );
      if (selectedElement && emailsContainerRef.current) {
        // If we're showing collapsed view, make sure all emails are visible first
        if (emails.length >= 10 && !showCollapsed) {
          setShowCollapsed(true);
        }

        // If element is found, scroll it into view
        selectedElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  }, [expandedEmailId, emails?.length, showCollapsed]);

  const visibleEmails = useMemo(() => {
    if (emails.length < 10 || showCollapsed) {
      return emails;
    }

    // Find the index of the clicked email
    const clickedEmailIndex = emails.findIndex(
      (email) => email.id === expandedEmailId,
    );

    // Always show first and last two emails
    const visibleIndices = new Set([0, emails.length - 2, emails.length - 1]);

    // Add clicked email if it's not already included and exists in the array
    if (clickedEmailIndex !== -1 && !visibleIndices.has(clickedEmailIndex)) {
      visibleIndices.add(clickedEmailIndex);
    }

    // Convert to sorted array and get emails
    return Array.from(visibleIndices)
      .sort((a, b) => a - b)
      .map((index) => emails[index]);
  }, [emails, showCollapsed, expandedEmailId]);

  // Calculate collapsed sections
  const getCollapsedInfo = useMemo(() => {
    if (emails.length < 10 || showCollapsed) {
      return { sections: [] };
    }

    const sections: { beforeIndex: number; count: number }[] = [];

    // Get visible email indices
    const visibleIndices = visibleEmails.map((email) =>
      emails.findIndex((e) => e.id === email.id),
    );

    // Add collapsed sections between visible emails
    for (let i = 0; i < visibleIndices.length - 1; i++) {
      const start = visibleIndices[i];
      const end = visibleIndices[i + 1];
      const count = end - start - 1;

      if (count > 0) {
        sections.push({
          beforeIndex: i + 1, // Index in visibleEmails array where to show the indicator
          count,
        });
      }
    }

    return { sections };
  }, [emails, visibleEmails, showCollapsed]);

  const toggleEmail = (emailId: string) => {
    // Toggle expansion state
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(emailId) && emailId === selectedEmailId) {
        next.delete(emailId);
      } else {
        next.add(emailId);
      }
      return next;
    });

    // Always update the selection
    setSelectedEmailId(emailId);
  };

  useEffect(() => {
    // Set initial state based on props
    setShowCollapsed(showCollapsed);

    // Reset expanded emails when emails change
    if (emails.length > 0) {
      // Prefer expandedEmailId if it exists in the emails list
      const found = emails.find((e) => e.id === expandedEmailId);
      setExpandedEmails(new Set([found ? found.id : emails[0].id]));
    } else {
      setExpandedEmails(new Set());
    }
  }, [emails, showCollapsed, expandedEmailId]);

  useEffect(() => {
    // If the last email is expanded and visible, scroll it into view
    if (!emails?.length) return;
    const lastEmail = emails[emails.length - 1];
    if (
      expandedEmails.has(lastEmail.id) &&
      visibleEmails.some((email) => email.id === lastEmail.id)
    ) {
      setTimeout(() => {
        const lastElement = document.querySelector(
          `[data-email-id="${lastEmail.id}"]`,
        );
        if (lastElement) {
          lastElement.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      }, 100);
    }
  }, [expandedEmails, emails, visibleEmails]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-xl font-bold">
            {activity.activity || "No Subject"}
          </h2>
          <ThreadSummary
            channel={activity.channel}
            threadSummary={emailThread?.thread_summary}
          />
        </div>
        <ThreadParticipants
          participants={emailThread?.thread_summary?.active_participants || []}
          accountId={accountId}
          getNameByEmailOverride={getNameByEmailOverride}
        />
      </div>

      <Separator className="my-6" />

      <div
        className="space-y-4 overflow-y-auto pr-4 flex-1"
        ref={emailsContainerRef}
      >
        <div className="pl-6">
          {visibleEmails.map((email, index) => {
            // Find if there should be a collapse indicator before this email
            const collapsedSection = getCollapsedInfo.sections.find(
              (section) => section.beforeIndex === index,
            );

            // Find if there's a collapse indicator after this email
            const hasCollapseAfter = getCollapsedInfo.sections.find(
              (section) => section.beforeIndex === index + 1,
            );

            return (
              <React.Fragment key={email.id}>
                {collapsedSection && (
                  <div className="relative">
                    <div className="absolute left-0 right-0 top-[45%] h-[1px] bg-border" />
                    <div className="absolute left-0 right-0 top-[55%] h-[1px] bg-border" />

                    <button
                      onClick={() => setShowCollapsed(true)}
                      className="relative flex items-center gap-2 mx-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center text-sm text-muted-foreground hover:text-accent-foreground group-hover:bg-accent relative">
                        <span className="group-hover:opacity-0 transition-opacity">
                          {collapsedSection.count}
                        </span>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronsUpDown className="h-4 w-4" />
                        </div>
                      </div>
                    </button>
                  </div>
                )}
                <div data-email-id={email.id}>
                  <EmailMessage
                    key={email.id}
                    email={email}
                    isExpanded={expandedEmails.has(email.id)}
                    onToggle={() => toggleEmail(email.id)}
                    showSeparator={
                      !hasCollapseAfter && index !== visibleEmails.length - 1
                    }
                    direction={email.direction}
                    isSelected={email.id === selectedEmailId}
                    accountId={accountId}
                    getNameByEmailOverride={getNameByEmailOverride}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
